import React, { Suspense } from 'react';
import { ResourceType } from '@cotera/api';
import { Text } from '@cotera/client/app/components/ui';
import { DatasetPageWithFetchedData } from '@cotera/client/app/v1/dataset/dataset.page';
import type { ArtifactRendererProps } from './types';
import { registerArtifactRenderer } from './registry';
import { ChatArtifactAccessGate } from './chat-artifact-access-gate';

type DatasetPayload = {
  datasetId?: string;
  /** Set by "New dataset": this id does not exist server-side yet. */
  isNew?: boolean;
};

const DatasetArtifactSkeleton: React.FC = () => (
  <div className="h-full w-full bg-background flex flex-col min-h-0 overflow-hidden">
    <div className="flex-1 min-h-0 overflow-y-auto p-5">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="h-10 w-2/3 rounded bg-coco-accent animate-pulse" />
        <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
        <div className="h-24 w-full rounded bg-coco-accent animate-pulse" />
      </div>
    </div>
  </div>
);

const DatasetArtifactRenderer: React.FC<ArtifactRendererProps> = ({
  artifact,
}) => {
  const payload = artifact.payload as DatasetPayload;
  const datasetId = payload?.datasetId ?? artifact.artifactId;

  if (datasetId === undefined || datasetId === '') {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background p-4">
        <Text.P className="text-muted-foreground">Dataset not found</Text.P>
      </div>
    );
  }

  // A brand new dataset has nothing to fetch and no permissions row yet, so it
  // skips the access gate the way the agent renderer does.
  if (payload?.isNew === true) {
    return <DatasetPageWithFetchedData id={datasetId} mode="artifact" isNew />;
  }

  return (
    <ChatArtifactAccessGate
      type={ResourceType.Dataset}
      id={datasetId}
      artifactType="dataset"
      fallback={<DatasetArtifactSkeleton />}
    >
      <Suspense fallback={<DatasetArtifactSkeleton />}>
        <DatasetPageWithFetchedData id={datasetId} mode="artifact" />
      </Suspense>
    </ChatArtifactAccessGate>
  );
};

registerArtifactRenderer('dataset', DatasetArtifactRenderer);
